import {useState, useEffect} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {Navigate, useParams, useNavigate} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import type {TimelineMatrix} from "../../../sdo/TimelineMatrix.ts";

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// TODO: Replace with API call to TimelineManager.getMatrix()
function buildTestData(): TimelineMatrix {
    return {
        rows: {
            2024: {total: 847, counts: {1: 42, 2: 58, 3: 91, 4: 76, 5: 63, 6: 88, 7: 105, 8: 72, 9: 54, 10: 81, 11: 67, 12: 50}},
            2025: {total: 623, counts: {1: 34, 2: 47, 3: 82, 4: 61, 5: 55, 6: 73, 7: 44, 8: 68, 9: 52, 10: 39, 11: 41, 12: 27}},
            2026: {total: 214, counts: {1: 52, 2: 48, 3: 67, 4: 47, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0}},
        }
    };
}

// --- Cell styles mirroring desktop card-timeline-* CSS ---

const headerCellSx = {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#02cfee",
    textAlign: "center" as const,
    padding: "6px 4px",
    backgroundColor: "rgba(0, 50, 100, 0.4)",
    borderRadius: "6px",
    userSelect: "none" as const,
};

const labelCellSx = {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#F2F5FA",
    textAlign: "center" as const,
    padding: "6px 4px",
    cursor: "pointer",
    borderRadius: "10px",
    "&:hover": {
        color: "#02cfee",
    },
};

const countCellSx = {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "#38ff82",
    textAlign: "center" as const,
    padding: "6px 4px",
    backgroundColor: "#091920",
    borderRadius: "10px",
    opacity: 0.7,
    cursor: "pointer",
    "&:hover": {
        opacity: 1.0,
    },
};

const zeroCellSx = {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "#3F464F",
    textAlign: "center" as const,
    padding: "6px 4px",
    backgroundColor: "#091920",
    borderRadius: "10px",
    opacity: 0.7,
    cursor: "default",
};

const totalCellSx = {
    ...countCellSx,
    fontWeight: 700,
};

function UXTimeline() {
    const {networkUID} = useParams();
    const navigate = useNavigate();
    const [matrix, setMatrix] = useState<TimelineMatrix | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // TODO: Replace with API call
        const data = buildTestData();
        setMatrix(data);
        setLoading(false);
    }, [networkUID]);

    const years = matrix ? Object.keys(matrix.rows).map(Number).sort((a, b) => a - b) : [];

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {!appSession.activeSession() ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title="Timeline"
                        breadcrumbs={[
                            {display: "Networks", linkTo: "/networks/", activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Timeline", linkTo: "", activeView: true},
                        ]}
                    />
                    <Stack sx={appStyles.sectionBox} spacing={0} width="100%" height="100%" alignItems="start">
                        {loading ? (
                            <Typography sx={{color: "#38ff82", fontSize: "0.85rem", padding: "16px"}}>Loading...</Typography>
                        ) : years.length === 0 ? (
                            <Typography sx={{color: "#a8b8c0", fontSize: "0.85rem", padding: "16px"}}>No timeline data found.</Typography>
                        ) : (
                            <Box sx={{width: "100%", overflowX: "auto", padding: "8px"}}>
                                <Box sx={{display: "grid", gridTemplateColumns: "70px 70px repeat(12, 1fr)", gap: "3px", minWidth: "800px"}}>
                                    {/* Header row */}
                                    <Typography sx={headerCellSx}>Year</Typography>
                                    <Typography sx={headerCellSx}>Total</Typography>
                                    {MONTH_SHORT.map(m => (
                                        <Typography key={m} sx={headerCellSx}>{m}</Typography>
                                    ))}

                                    {/* Data rows */}
                                    {years.map(year => {
                                        const row = matrix!.rows[year];
                                        return [
                                            <Typography
                                                key={`y-${year}`}
                                                sx={labelCellSx}
                                                onClick={() => navigate(`/networks/${networkUID}/timeline/${year}`)}
                                            >
                                                {year}
                                            </Typography>,
                                            <Typography
                                                key={`t-${year}`}
                                                sx={totalCellSx}
                                                onClick={() => navigate(`/networks/${networkUID}/timeline/${year}`)}
                                            >
                                                {row.total}
                                            </Typography>,
                                            ...MONTH_SHORT.map((_, mi) => {
                                                const count = row.counts[mi + 1] ?? 0;
                                                const monthIndex = mi + 1;
                                                return (
                                                    <Typography
                                                        key={`m-${year}-${mi}`}
                                                        sx={count > 0 ? countCellSx : zeroCellSx}
                                                        onClick={count > 0 ? () => navigate(`/networks/${networkUID}/timeline/${year}/${monthIndex}`) : undefined}
                                                    >
                                                        {count}
                                                    </Typography>
                                                );
                                            }),
                                        ];
                                    })}
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default UXTimeline;
